import React from 'react';
import { useIntl } from 'react-intl';
import { Alert, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { ServiceConnection } from '../../../../redux/api/fymAPI.types';
import { PhoneConnectHandoff } from '../../../mobileApps/PhoneConnectHandoff';
import { catalogConnectPath } from '../../../mobileApps/mobileAppLinks';
import { catalogServiceName } from '../earlyAccess/earlyAccessCopy';
import { isDittoCaptureCard } from './sessionCaptureCardState';
import { captureMessages, sessionCaptureMessages as messages } from './sessionCaptureMessages';

interface SessionCaptureHandoffModalProps {
  accountId: number;
  /** The card the handoff is for. Undefined closes the modal. */
  connection?: ServiceConnection;
  onClose: () => void;
}

/**
 * The web answer to "Connect" on Ditto Music: the login happens on Ditto's own page inside the
 * Music24 phone app, so the web shows the phone handoff with the Google hint.
 *
 * design: your-intelligence k2 (with the handoff of music24-mobile-apps 1a)
 */
const SessionCaptureHandoffModalComponent: React.FC<SessionCaptureHandoffModalProps> = ({
  accountId,
  connection,
  onClose,
}) => {
  const intl = useIntl();
  if (connection === undefined) {
    return null;
  }
  const { integrationType } = connection;
  const isReconnect = connection.status === ServiceConnectionStatus.expired;
  const isDitto = isDittoCaptureCard(connection);
  const service = catalogServiceName(intl, integrationType);
  let title = intl.formatMessage(isReconnect ? captureMessages.reconnectTitle : captureMessages.connectTitle, {
    service,
  });
  if (isDitto) {
    title = intl.formatMessage(isReconnect ? messages.reconnectTitle : messages.connectTitle);
  }

  return (
    <Modal isOpen toggle={onClose} centered size="lg" className="sml-modal">
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        <PhoneConnectHandoff
          accountId={accountId}
          integrationType={integrationType}
          serviceName={service}
          onClose={onClose}
          next={catalogConnectPath(integrationType)}
          logsInOnServicePage
          hint={
            isDitto ? (
              <Alert color="info" className="mb-0 mt-3">
                {intl.formatMessage(messages.googleHint)}
              </Alert>
            ) : undefined
          }
        />
      </ModalBody>
    </Modal>
  );
};

export const SessionCaptureHandoffModal = TypedMemo(SessionCaptureHandoffModalComponent);
